@import 'apps/webcomponents/src/assets/common.scss';
@import 'apps/webcomponents/src/assets/color-helpers.scss';
@import 'apps/webcomponents/src/assets/scss/mixins';

:host {
  display: inline-block;
  position: relative;
  width: fit-content;
  height: fit-content;
  --button-max-width: unset;
}

gz-select {
  display: inline-block;
}

button {
  border: 0;
  cursor: pointer;
  font-size: var(--button-font-size);
  line-height: 1;
  border-radius: var(--button-border-radius);
  text-align: center;
  padding: 0 calc(2 * var(--spacer));
  transition: all 0.3s ease;
  height: var(--button-height);
  min-width: calc(2.25 * var(--button-height));
  max-width: var(--button-max-width, unset);
  font-weight: 500;
  background-color: transparent;
  border: 1px solid;
  border-color: inherit;
  text-transform: var(--button-text-transform, uppercase);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
  }

  &.full {
    &.primary {
      border-color: transparent;
      color: var(--white-layout-300-primary);
      background-color: var(--blue-layout-403-primary);

      .button-spinner {
        --bar-color: transparent;
        --stroke-color: var(--white-layout-300-primary);
      }

      gz-icon::part(svgIcon) {
        fill: var(--white-layout-300-primary);
      }
    }

    &.negative {
      border-color: var(--red-layout-302-primary);
      color: var(--white-layout-300-primary);
      background-color: var(--red-layout-302-primary);
    }

    &:hover {
      &.primary {
        border-color: transparent;
        background-color: var(--blue-layout-308-active-hc);

        gz-icon::part(svgIcon) {
          fill: var(--blue-layout-301-active);
        }
      }

      &.negative {
        border-color: transparent;
        background-color: var(--red-layout-200-hover);
      }
    }

    &:focus-visible {
      &.primary {
        @include focus-outline-state-mixin(var(--blue-layout-500-focus));
      }

      &.negative {
        border-color: var(--red-layout-300-primary);
        background-color: var(--red-layout-051-background);
        color: var(--red-layout-303-primary);
        @include focus-outline-state-mixin(var(--blue-layout-500-focus));

        .button-spinner {
          --bar-color: transparent;
          --stroke-color: var(--red-layout-303-primary);
        }
      }
    }

    &:active {
      &.primary {
        border-color: var(--blue-layout-501-focus);
        background-color: var(--blue-layout-501-focus);
      }

      &.negative {
        border-color: var(--red-layout-303-primary);
        color: var(--red-layout-303-primary);
        background-color: var(--red-layout-051-background);

        .button-spinner {
          --bar-color: transparent;
          --stroke-color: var(--red-layout-303-primary);
        }
      }
    }
  }

  &.outline {
    background-color: transparent;
    &.primary {
      border-color: var(--blue-layout-400-primary);
      color: var(--blue-layout-400-primary);
      .button-spinner {
        --bar-color: transparent;
        --stroke-color: var(--blue-layout-400-primary);
      }
    }

    &.secondary {
      border-color: var(--body-text-arrows-400);
      color: var(--body-text-arrows-400);
      .button-spinner {
        --bar-color: transparent;
        --stroke-color: var(--body-text-arrows-400);
      }
    }

    &.negative {
      border-color: var(--red-layout-302-primary);
      color: var(--red-layout-300-primary);
      background-color: var(--white-layout-002-light-background-on-dark-theme);
      .button-spinner {
        --bar-color: transparent;
        --stroke-color: var(--red-layout-303-primary);
      }
    }

    &.white {
      border-color: var(--white-layout-201-secondary-on-dark-background);
      color: var(--white-layout-300-primary);
      .button-spinner {
        --bar-color: transparent;
        --stroke-color: var(--white-layout-201-secondary-on-dark-background);
      }
    }

    &:hover {
      &.primary {
        border-color: var(--blue-layout-300-active);
        color: var(--blue-layout-307-active-hc);
        background-color: var(--blue-layout-050-selected);
      }

      &.negative {
        border-color: var(--red-layout-300-primary);
        color: var(--red-layout-303-primary);
        background-color: var(--red-layout-051-background);
      }

      &.secondary {
        color: var(--body-text-arrows-400);
        background-color: var(--chart-050-hover);
      }

      &.white {
        border-color: var(--white-layout-300-primary);
        background-color: var(--white-layout-011-hover-on-blue-background);
      }
    }

    &:focus-visible {
      &.primary {
        color: var(--blue-layout-400-primary);
        border-color: var(--blue-layout-400-primary);
        background-color: transparent;
        @include focus-outline-state-mixin(var(--blue-layout-500-focus));
      }

      &.secondary {
        color: var(--body-text-arrows-400);
        border-color: var(--body-text-arrows-400);
        background-color: var(--blue-layout-302-active-focus);
        @include focus-outline-state-mixin(var(--blue-layout-500-focus));
      }

      &.negative {
        color: var(--red-layout-300-primary);
        border-color: var(--red-layout-201-hover);
        background-color: var(--white-layout-002-light-background-on-dark-theme);
        @include focus-outline-state-mixin(var(--blue-layout-500-focus));
      }

      &.white {
        background-color: var(--white-layout-302-active-focus);
        border-color: var(--white-layout-101-outline-on-dark-background);
        @include focus-outline-state-mixin(var(--blue-layout-500-focus));
      }
    }

    &:active {
      &.secondary {
        color: var(--blue-layout-503-focus-on-dark-background-hc);
        border-color: var(--blue-layout-501-focus);
        background-color: var(--blue-layout-302-active-focus);
      }

      &.primary {
        color: var(--blue-layout-503-focus-on-dark-background-hc);
        border-color: var(--blue-layout-501-focus);
        background-color: var(--blue-layout-302-active-focus);
      }

      &.negative {
        color: var(--red-layout-300-primary);
        border-color: var(--red-layout-201-hover);
        background-color: var(--white-layout-002-light-background-on-dark-theme);
      }

      &.white {
        background-color: var(--white-layout-302-active-focus);
        border-color: var(--white-layout-101-outline-on-dark-background);
      }
    }
  }

  &.quiet {
    border-color: transparent;
    min-width: unset;

    &.primary {
      color: var(--blue-layout-400-primary);
    }

    &.secondary {
      color: var(--body-text-arrows-400);
    }

    &.negative {
      color: var(--red-layout-300-primary);
    }

    &:hover {
      &.primary {
        color: var(--blue-layout-307-active-hc);
        background-color: var(--blue-layout-050-selected);
        text-decoration: underline;
      }
      &.negative {
        color: var(--red-layout-303-primary);
        background-color: var(--red-layout-051-background);
      }
      &.secondary {
        color: var(--body-text-arrows-400);
        background-color: var(--chart-050-hover);
      }
    }

    &:focus-visible {
      &.primary {
        border-color: transparent;
        color: var(--blue-layout-400-primary);
        background-color: var(--blue-layout-302-active-focus);
        @include focus-outline-state-mixin(var(--blue-layout-500-focus));
      }

      &.secondary {
        border-color: transparent;
        color: var(--body-text-arrows-400);
        background-color: var(--blue-layout-302-active-focus);
        @include focus-outline-state-mixin(var(--blue-layout-500-focus));
      }

      &.negative {
        border-color: transparent;
        color: var(--red-layout-300-primary);
        background-color: var(--white-layout-002-light-background-on-dark-theme);
        @include focus-outline-state-mixin(var(--blue-layout-500-focus));
      }
    }

    &:active {
      &.primary {
        color: var(--blue-layout-503-focus-on-dark-background-hc);
        background-color: var(--blue-layout-302-active-focus);
      }

      &.secondary {
        color: var(--blue-layout-503-focus-on-dark-background-hc);
        background-color: var(--blue-layout-302-active-focus) !important;
      }

      &.negative {
        color: var(--red-layout-300-primary);
        background-color: var(--white-layout-002-light-background-on-dark-theme);
      }
    }
  }

  &.dark:not(.outline) {
    /* TODO: implement quiet, button-spinner, hover, disabled, active, and focus
             for secondary and negative colors (needs Design System updates) */
    background-color: var(--white-layout-300-primary);
    border-color: transparent;

    &.primary {
      color: var(--blue-layout-403-primary);
      border-color: transparent;

      &:hover {
        color: var(--blue-layout-404-primary);
        background-color: var(--white-layout-250-disabled-text-on-blue-background);
      }
      &:focus-visible {
        @include focus-outline-state-mixin(var(--blue-layout-500-focus));
      }
      &:active {
        background-color: var(--blue-layout-501-focus);
        color: var(--white-layout-300-primary);
      }
      .button-spinner {
        --bar-color: transparent;
        --stroke-color: unset;
      }
    }

    &.secondary {
      color: var(--body-text-arrows-400);
    }

    &.negative {
      color: var(--red-layout-300-primary);
    }

    &.quiet {
      background-color: transparent;
      color: var(--white-layout-300-primary);

      &.primary {
        &:hover {
          color: var(--white-layout-300-primary);
          background-color: var(--white-layout-011-hover-on-blue-background);
        }
        &:focus-visible {
          @include focus-outline-state-mixin(var(--blue-layout-500-focus));
          background-color: var(--blue-layout-302-active-focus);
        }
        &:active {
          color: var(--white-layout-300-primary);
          background-color: var(--blue-layout-501-focus);
        }
      }
    }
  }

  .button-text {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    --font-family: 'Montserrat', sans-serif;
  }
}

:host(:focus) {
  border: none;
  outline: none;
}

:host([lowercase]) {
  button {
    text-transform: none;
  }
}

:host([buttonFontSize='small']) {
  button {
    font-size: var(--small-button-font-size);
    line-height: 0;
  }
}

:host([buttonHeight='small']) {
  button {
    height: var(--small-button-height);
    &.quiet {
      height: var(--body-size-xxlarge);
      border-radius: var(--quiet-button-border-radius);
      padding: 0 var(--spacer);

      &:hover {
        background-color: transparent;
        &.primary {
          color: var(--blue-layout-308-active-hc);
        }

        &.secondary {
          color: var(--chart-301-icon-buttons);
        }

        &.negative {
          color: var(--red-layout-303-primary);
        }
      }

      &:focus-visible {
        outline: initial;

        &.primary {
          color: var(--blue-layout-503-focus-on-dark-background-hc);
        }

        &.secondary {
          color: var(--body-text-arrows-400);
        }

        &.primary,
        &.secondary {
          border-color: transparent;
          background-color: var(--blue-layout-302-active-focus) !important;
        }

        &.negative {
          border-color: var(--red-layout-201-hover);
          background: var(--white-layout-002-light-background-on-dark-theme);
        }
      }

      &:active {
        &.primary {
          color: var(--blue-layout-503-focus-on-dark-background-hc);
          background-color: var(--blue-layout-302-active-focus) !important;
        }
        &.negative {
          border-color: var(--red-layout-201-hover);
          color: var(--red-layout-303-primary);
          background: var(--white-layout-002-light-background-on-dark-theme);
        }

        &.secondary {
          color: var(--chart-301-icon-buttons);
        }
      }
    }
  }
}

:host([buttonFontSize='small'], [buttonHeight='small']) {
  button {
    &.quiet {
      border-radius: var(--quiet-button-border-radius);
      padding: var(spacer);
      &:focus-visible {
        &.primary,
        &.secondary {
          border-color: transparent;
          @include focus-outline-state-mixin(var(--blue-layout-500-focus));
        }
        &.negative {
          border-color: transparent;
        }
      }
    }
  }
}

:host([active]) {
  button {
    &.full {
      &.primary {
        border-color: var(--blue-layout-301-active);
        background-color: var(--blue-layout-301-active);
      }
    }
  }
}

:host([disabled]) {
  pointer-events: none;
  transition: none;
  button {
    cursor: default;

    &.full {
      &.primary {
        color: var(--white-layout-305-disabled-text);
        border-color: transparent;
        background-color: var(--blue-layout-200-disabled);
      }
      &.negative {
        color: var(--white-layout-304-text);
        border-color: transparent;
        background-color: var(--red-layout-102-disabled);
      }
    }
    &.outline {
      &.primary {
        color: var(--blue-layout-202-disabled);
        border-color: var(--blue-layout-202-disabled);
        background-color: transparent;
      }
      &.negative {
        color: var(--red-layout-101-disabled);
        border-color: var(--red-layout-101-disabled);
        background-color: transparent;
      }
      &.secondary {
        color: var(--grey-layout-200-disabled);
        border-color: var(--grey-layout-200-disabled);
        background-color: transparent;
      }
      &.white {
        color: var(--white-layout-101-outline-on-dark-background);
        border-color: var(--white-layout-101-outline-on-dark-background);
        background-color: transparent;
      }
    }
    &.quiet {
      &.primary {
        color: var(--blue-layout-202-disabled);
      }
      &.negative {
        color: var(--red-layout-101-disabled);
      }
      &.secondary {
        color: var(--grey-layout-200-disabled);
      }
    }

    &.dark {
      &.primary {
        color: var(--white-layout-250-disabled-text-on-blue-background);
        background-color: var(--white-layout-005-background-for-icon-menu);
      }
      &.quiet {
        color: var(--white-layout-101-outline-on-dark-background);
        background-color: transparent;
      }
    }
  }
}

.button-spinner {
  --bar-color: transparent;
  --stroke-color: white;
}

.wrap-split-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

:host([type='outline'].split-button) {
  button {
    border-right: none;
  }
}
:host(.split-button) {
  button {
    border-radius: calc(2 * var(--spacer)) 0 0 calc(2 * var(--spacer));
  }
}

:host([type='link']) {
  button {
    border: 0;
    border-radius: 0;
    padding: 0;
    text-decoration: underline;
    height: auto !important;
    text-transform: none;

    &.primary {
      color: var(--blue-layout-400-primary);
    }

    &.secondary {
      color: var(--body-text-arrows-400);
    }
  }
}

:host([type='link']) {
  button {
    &.primary {
      &:hover {
        color: var(--blue-layout-301-active);
      }

      &:focus-visible {
        color: var(--blue-layout-500-focus);
      }

      &:active {
        color: var(--blue-layout-500-focus);
      }
    }

    &.secondary {
      &:hover {
        color: var(--chart-304-icon-buttons);
      }

      &:focus-visible {
        color: var(--body-text-arrows-400);
      }

      &:active {
        color: var(--body-text-arrows-400);
      }
    }
  }
}

.split-button-container {
  display: flex;

  .split-button[type='full'] + .separator {
    background-color: var(--blue-layout-403-primary);
    height: var(--button-height);
    display: flex;
    align-items: center;

    &::after {
      content: '';
      width: 1px;
      height: 70%;
      background-color: var(--white-layout-101-outline-on-dark-background);
    }
  }

  .split-button:not([type='full']) + .separator {
    display: none;
  }
}

::slotted(.icon) {
  font-size: 32px;
}
::slotted(.button-text) {
  display: inline-block;
}

:host(.inline-text) {
  button {
    width: 100%;
  }
  .button-text {
    display: inline;
  }
}
